class AppDirectorySearch extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Initialize elements
    this.searchInput = this.querySelector('input[type="search"]');
    this.searchForm = this.querySelector('form');
    this.resultsContainer = document.querySelector('.app-directory-grid');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeSearch());
    } else {
      this.initializeSearch();
    }
  }

  initializeSearch() {
    this.appCards = this.resultsContainer?.querySelectorAll('.app-directory-card');
    this.activeFilters = new Set();
    
    if (!this.searchInput || !this.searchForm || !this.resultsContainer || !this.appCards?.length) {
      console.error('Required elements not found for app directory search');
      return;
    }

    this.initializeFilters();
    this.bindEvents();
    this.initializeCardClicks();
  }

  initializeFilters() {
    // Create filter chips container
    const filterContainer = document.createElement('div');
    filterContainer.className = 'app-directory-filters chips-container';
    
    // Add "All" chip first
    const allChip = document.createElement('button');
    allChip.className = 'chip chip--filter';
    allChip.setAttribute('data-category', 'all');
    allChip.setAttribute('role', 'checkbox');
    allChip.setAttribute('aria-checked', 'true'); // Active by default
    allChip.textContent = 'All';
    allChip.addEventListener('click', () => this.toggleFilter(allChip));
    filterContainer.appendChild(allChip);
    
    // Get unique categories
    const categories = new Set();
    this.appCards.forEach(card => {
      const category = card.dataset.category;
      if (category) categories.add(category);
    });
    
    // Add chips for each category
    categories.forEach(category => {
      const chip = document.createElement('button');
      chip.className = 'chip chip--filter';
      chip.setAttribute('data-category', category);
      chip.setAttribute('role', 'checkbox');
      chip.setAttribute('aria-checked', 'false');
      chip.textContent = category;
      
      chip.addEventListener('click', () => this.toggleFilter(chip));
      filterContainer.appendChild(chip);
    });

    // Insert filters after search form
    this.searchForm.parentNode.insertBefore(filterContainer, this.searchForm.nextSibling);
  }

  toggleFilter(chipElement) {
    const category = chipElement.dataset.category;
    const isActive = chipElement.getAttribute('aria-checked') === 'true';
    
    // Special handling for "All" filter
    if (category === 'all') {
      // When clicking "All", deactivate all other filters
      if (!isActive) {
        const allChips = this.searchForm.parentNode.querySelectorAll('.chip--filter');
        allChips.forEach(chip => {
          chip.setAttribute('aria-checked', 'false');
        });
        chipElement.setAttribute('aria-checked', 'true');
        this.activeFilters.clear();
      }
    } else {
      // When clicking other filters, deactivate "All"
      const allChip = this.searchForm.parentNode.querySelector('.chip--filter[data-category="all"]');
      allChip.setAttribute('aria-checked', 'false');
      
      chipElement.setAttribute('aria-checked', (!isActive).toString());
      
      if (!isActive) {
        this.activeFilters.add(category);
      } else {
        this.activeFilters.delete(category);
        
        // If no filters are active, activate "All"
        if (this.activeFilters.size === 0) {
          allChip.setAttribute('aria-checked', 'true');
        }
      }
    }
    
    this.performSearch();
  }

  initializeCardClicks() {
    this.appCards.forEach(card => {
      this.hideUrlTextNodes(card);

      // Handle main card click
      const cardLink = card.querySelector('.card-link');
      if (cardLink) {
        cardLink.addEventListener('click', (e) => {
          // Don't trigger card click if clicking on accordion or collection chip
          if (!e.target.closest('summary') && !e.target.closest('.chip--product')) {
            e.preventDefault();
            const url = card.dataset.url;
            if (url && url.includes('https://')) {  // Validate URL
              window.open(url, '_blank');
            }
          }
        });
      }

      // Make cards keyboard accessible
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // Only trigger if not pressing on accordion or collection chip
          if (!e.target.closest('summary') && !e.target.closest('.chip--product')) {
            e.preventDefault();
            const url = card.dataset.url;
            if (url && url.includes('https://')) {  // Validate URL
              window.open(url, '_blank');
            }
          }
        }
      });

      // Prevent accordion clicks from triggering card navigation
      const summaries = card.querySelectorAll('summary');
      summaries.forEach(summary => {
        summary.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      });

      // Handle collection chip clicks
      const collectionChips = card.querySelectorAll('.chip--product');
      collectionChips.forEach(chip => {
        chip.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent card click
          const collectionUrl = chip.dataset.url;
          if (collectionUrl) {
            window.open(collectionUrl, '_blank');
          }
        });

        // Make chips keyboard accessible
        chip.setAttribute('role', 'link');
        chip.setAttribute('tabindex', '0');
        chip.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation(); // Prevent card activation
            const collectionUrl = chip.dataset.url;
            if (collectionUrl) {
              window.open(collectionUrl, '_blank');
            }
          }
        });
      });
    });
  }

  hideUrlTextNodes(element) {
    // Walk through all text nodes
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let node;
    const urlPattern = /https?:\/\/[^\s]+/g;
    
    while ((node = walker.nextNode())) {
      if (urlPattern.test(node.textContent)) {
        // If the text node only contains a URL, remove it
        if (node.textContent.trim().match(urlPattern)?.[0] === node.textContent.trim()) {
          node.textContent = '';
        }
      }
    }
  }

  bindEvents() {
    this.searchForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.performSearch();
    });

    this.searchInput.addEventListener('input', this.debounce(() => {
      this.performSearch();
    }, 300));

    this.searchInput.addEventListener('search', () => {
      if (this.searchInput.value === '') {
        this.performSearch();
      }
    });
  }

  performSearch() {
    const searchTerm = this.searchInput.value.toLowerCase().trim();
    let hasVisibleCards = false;

    this.appCards.forEach(card => {
      let matches = false;

      // Check if card matches both search term and active filters
      if (!searchTerm && this.activeFilters.size === 0) {
        matches = true;
      } else {
        const cardCategory = card.dataset.category;
        const matchesFilters = this.activeFilters.size === 0 || this.activeFilters.has(cardCategory);
        
        if (matchesFilters) {
          if (!searchTerm) {
            matches = true;
          } else {
            const searchableContent = [
              card.dataset.name,
              card.dataset.category,
              card.querySelector('.card__description')?.textContent,
              ...Array.from(card.querySelectorAll('.chip')).map(chip => chip.textContent)
            ].filter(Boolean).join(' ').toLowerCase();

            matches = searchableContent.includes(searchTerm);
          }
        }
      }

      card.classList.toggle('hidden', !matches);
      card.setAttribute('aria-hidden', !matches);
      if (matches) hasVisibleCards = true;
    });

    this.updateNoResultsMessage(!hasVisibleCards && (searchTerm || this.activeFilters.size > 0));
  }

  updateNoResultsMessage(show) {
    let noResultsElement = this.resultsContainer.querySelector('.no-results-message');
    
    if (show) {
      if (!noResultsElement) {
        noResultsElement = document.createElement('div');
        noResultsElement.className = 'no-results-message';
        noResultsElement.setAttribute('role', 'status');
        this.resultsContainer.prepend(noResultsElement);
      }
      noResultsElement.textContent = `No apps found matching "${this.searchInput.value}"`;
    } else if (noResultsElement) {
      noResultsElement.remove();
    }
  }

  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

customElements.define('app-directory-search', AppDirectorySearch); 